
import Vue from 'vue'
import yaml from 'js-yaml'
import { apiService } from '@/service/api.service'

const EMPTY = {
  name: '',
  description: '',
  engine_id: null,
  model_name: '',
  served_model_name: '',
  container_image: '',
  supported_node_counts: [] as any[],
  tensor_parallel: '',
  data_parallel: '',
  expert_parallel: '',
  pipeline_parallel: '',
  tokens_per_second: '',
  tokens_per_second_source: 'manual',
  recipe_yaml: '',
  is_active: true
}

export default Vue.extend({
  name: 'SparkrunRecipeDialog',
  props: {
    value: { type: Boolean, default: false },
    recipe: { type: Object, default: null },
    engines: { type: Array, default: () => [] }
  },
  data () {
    return {
      form: { ...EMPTY },
      errors: {} as any,
      saveError: '',
      saving: false,
      // What the last read off the YAML found, and whether it worked.
      yamlNotice: '',
      yamlNoticeType: 'info',
      // Set while `reset()` repopulates the form, so opening the dialog on an
      // existing recipe never rewrites it from its own stored YAML.
      hydrating: false
    }
  },
  computed: {
    isEdit (): boolean {
      return !!(this.recipe && (this.recipe as any).id)
    },
    /**
     * Machines this recipe occupies: tensor x pipeline x data parallelism.
     *
     * An unset dimension counts as 1 -- "no pipeline parallelism" is one stage,
     * so treating it as 0 would say the recipe needs no machines at all.
     */
    /** Says where the engine came from, so an auto-pick is never mistaken for a stored one. */
    engineHint (): string {
      if (!this.engines.length) return 'No engines are defined yet.'
      return "Matched from the recipe's `runtime` when it names one of these."
    },
    derivedNodeCount (): number {
      const f: any = this.form
      const one = (v: any) => {
        const n = parseInt(v, 10)
        return Number.isInteger(n) && n > 0 ? n : 1
      }
      return one(f.tensor_parallel) * one(f.pipeline_parallel) * one(f.data_parallel)
    }
  },
  watch: {
    value (open: boolean) {
      if (open) this.reset()
    },
    'form.recipe_yaml' () {
      if (this.hydrating) return
      this.readFromYaml(false)
    }
  },
  methods: {
    dash (value: any): string {
      const n = parseInt(value, 10)
      return Number.isInteger(n) && n > 0 ? String(n) : '-'
    },
    /**
     * Read the recipe's own fields off the pasted YAML.
     *
     * sparkrun recipes carry everything the form asks for except a name --
     * `name:` is ignored by the CLI, which takes the name from the *filename*
     * (core/recipe.py). So a name is derived, and like every other value it is
     * only written into a field the admin has left empty. `force` (the button)
     * overwrites instead, for when the YAML has been swapped for another recipe.
     */
    readFromYaml (force: boolean) {
      const text = String(this.form.recipe_yaml || '').trim()
      if (!text) {
        this.yamlNotice = ''
        return
      }

      let parsed: any
      try {
        parsed = yaml.load(text)
      } catch (err: any) {
        // Not an error state while typing: the YAML is simply not complete yet,
        // and the field's own validation is what refuses to save a broken one.
        this.yamlNotice = force ? `Could not parse the YAML: ${err.message || err}` : ''
        this.yamlNoticeType = 'warning'
        return
      }
      if (!parsed || typeof parsed !== 'object') {
        this.yamlNotice = force ? 'The YAML did not parse into a recipe.' : ''
        this.yamlNoticeType = 'warning'
        return
      }

      const f: any = this.form
      const meta: any = (parsed.metadata && typeof parsed.metadata === 'object') ? parsed.metadata : {}
      const defaults: any = (parsed.defaults && typeof parsed.defaults === 'object') ? parsed.defaults : {}
      const filled: string[] = []

      const put = (key: string, value: any, label: string) => {
        if (value === null || value === undefined || value === '') return
        const current = f[key]
        if (!force && current !== '' && current !== null && current !== undefined) return
        if (String(current) === String(value)) return
        f[key] = String(value)
        filled.push(label)
      }

      put('name', this.deriveName(parsed, defaults, meta), 'name')
      put('description', parsed.description || meta.description, 'description')
      put('model_name', parsed.model, 'model')
      put('container_image', parsed.container, 'container image')
      put('served_model_name', defaults.served_model_name, 'served model name')
      put('tensor_parallel', defaults.tensor_parallel, 'TP')
      put('pipeline_parallel', defaults.pipeline_parallel, 'PP')
      put('data_parallel', defaults.data_parallel, 'DP')
      put('expert_parallel', defaults.expert_parallel, 'EP')

      const engineId = this.matchEngineByRuntime(parsed.runtime)
      if (engineId !== null && (force || !f.engine_id) && f.engine_id !== engineId) {
        f.engine_id = engineId
        filled.push('engine')
      }

      if (filled.length) {
        this.yamlNoticeType = 'info'
        this.yamlNotice = `Read from the recipe: ${filled.join(', ')}.`
      } else if (force) {
        this.yamlNoticeType = 'info'
        this.yamlNotice = 'Nothing new to read — every field already matches the recipe.'
      }

      if (parsed.runtime && this.matchEngineByRuntime(parsed.runtime) === null) {
        this.yamlNoticeType = 'warning'
        this.yamlNotice = `${this.yamlNotice} No engine is named '${parsed.runtime}', so pick one by hand.`.trim()
      }
    },
    /**
     * A name for the recipe, since the YAML does not carry one.
     *
     * `<served_model_name>-<runtime>` is the convention the public registry
     * already follows (`qwen3-1.7b` + `vllm` -> `qwen3-1.7b-vllm`), so a recipe
     * imported here lands on the same name it would have upstream.
     */
    deriveName (parsed: any, defaults: any, meta: any): string {
      if (parsed.name) return String(parsed.name)
      if (meta.name) return String(meta.name)
      const base = defaults.served_model_name ||
        (parsed.model ? String(parsed.model).split('/').pop() : '')
      if (!base) return ''
      const slug = String(base).trim().toLowerCase()
        .replace(/[^a-z0-9.-]+/g, '-')
        .replace(/^-+|-+$/g, '')
      const runtime = String(parsed.runtime || '').trim().toLowerCase()
      if (!slug) return ''
      return runtime && !slug.endsWith(runtime) ? `${slug}-${runtime}` : slug
    },
    /**
     * The engine whose name the recipe's `runtime` refers to, or null.
     *
     * Exact match first; a prefix match then covers the runtime variants that
     * hang off one engine (`vllm-ray` is still vLLM). Guarded on length so a
     * stray one- or two-letter runtime cannot claim an engine.
     */
    matchEngineByRuntime (runtime: any): number | null {
      const norm = (v: any) => String(v || '').toLowerCase().replace(/[^a-z0-9]/g, '')
      const r = norm(runtime)
      if (!r) return null
      const engines: any[] = this.engines as any[]
      const exact = engines.find((e) => norm(e.name) === r)
      if (exact) return exact.id
      if (r.length < 3) return null
      const prefix = engines.find((e) => {
        const n = norm(e.name)
        return n.length >= 3 && (n.startsWith(r) || r.startsWith(n))
      })
      return prefix ? prefix.id : null
    },
    reset () {
      this.errors = {}
      this.saveError = ''
      this.yamlNotice = ''
      this.yamlNoticeType = 'info'
      this.hydrating = true
      this.$nextTick(() => { this.hydrating = false })
      if (!this.recipe) {
        this.form = { ...EMPTY, supported_node_counts: [] }
        return
      }
      const r: any = this.recipe
      this.form = {
        name: r.name || '',
        description: r.description || '',
        engine_id: r.engine_id,
        model_name: r.model_name || '',
        served_model_name: r.served_model_name || '',
        container_image: r.container_image || '',
        supported_node_counts: (r.supported_node_counts || []).map((n: any) => String(n)),
        // Empty string, not 0: the field has to come back as "unset".
        tensor_parallel: r.tensor_parallel === null || r.tensor_parallel === undefined ? '' : String(r.tensor_parallel),
        data_parallel: r.data_parallel === null || r.data_parallel === undefined ? '' : String(r.data_parallel),
        expert_parallel: r.expert_parallel === null || r.expert_parallel === undefined ? '' : String(r.expert_parallel),
        pipeline_parallel: r.pipeline_parallel === null || r.pipeline_parallel === undefined ? '' : String(r.pipeline_parallel),
        tokens_per_second: r.tokens_per_second === null || r.tokens_per_second === undefined ? '' : String(r.tokens_per_second),
        tokens_per_second_source: r.tokens_per_second_source || 'manual',
        recipe_yaml: r.recipe_yaml || '',
        is_active: r.is_active !== false
      }
    },
    close () {
      this.$emit('input', false)
    },
    validate () {
      this.errors = {}
      if (!String(this.form.name || '').trim()) this.errors.name = 'A name is required'
      if (!String(this.form.recipe_yaml || '').trim()) {
        this.errors.recipe_yaml = 'The recipe YAML cannot be empty'
      }
      return Object.keys(this.errors).length === 0
    },
    payload () {
      return {
        ...this.form,
        supported_node_counts: (this.form.supported_node_counts || [])
          .map((n: any) => parseInt(String(n).trim(), 10))
          .filter((n: number) => !isNaN(n) && n > 0)
      }
    },
    async save () {
      if (!this.validate()) return
      this.saving = true
      this.saveError = ''
      try {
        if (this.isEdit) {
          await apiService.put(`/admin/sparkrun/recipes/${(this.recipe as any).id}`, this.payload())
        } else {
          await apiService.post('/admin/sparkrun/recipes', this.payload())
        }
        this.$emit('saved')
      } catch (err: any) {
        this.saveError =
          (err && err.response && err.response.data && err.response.data.error) ||
          'Could not save the recipe.'
      } finally {
        this.saving = false
      }
    }
  }
})
